import React, { useState } from "react";
import { ExternalLink, Github, ArrowUpRight, ArrowRight, Sparkles, X } from "lucide-react";
import { projects } from "../mock";
import Reveal from "./Reveal";
import SectionHeader from "./SectionHeader";
import DepthCarousel from "./DepthCarousel";

const Projects = () => {
  const [selected, setSelected] = useState(null);

  const items = (projects || []).map((p) => ({
    image: p.image,
    alt: p.title,
    ...p,
  }));

  const open = (url) => {
    if (url && url !== "#") window.open(url, "_blank", "noopener,noreferrer");
  };

  const openDetail = (index) => {
    const project = items[index];
    if (project) setSelected(project);
  };

  return (
    <section id="projects" className="relative py-10 md:py-12 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="03."
          eyebrow="Projects"
          title="Featured work & experiments"
          description="Real-world AI applications spanning voice AI, agentic systems, deep learning, and full-stack platforms."
        />

        <div className="relative" style={{ height: "520px" }}>
          <DepthCarousel
            items={items}
            depth={180}
            spread={100}
            tilt={22}
            tiltDirection="right"
            perspective={1400}
            visibleCards={4}
            falloff={0.22}
            blur={4}
            autoplay={false}
            loop
            cardWidth={340}
            cardHeight={470}
            radius={18}
            tint="#0a0b10"
            duration={700}
            ease="power3.out"
            showControls
            showIndicators
            renderCard={(item) => (
              <>
                <img className="depth-carousel__img" src={item.image} alt={item.alt || ""} draggable={false} />
                <div className="depth-carousel__label">
                  <span className="depth-carousel__label-index">
                    {String(item.id).padStart(2, "0")}
                  </span>
                  <span className="depth-carousel__label-title">{item.title}</span>
                  <span className="depth-carousel__label-cta">
                    View details
                    <ArrowUpRight size={13} />
                  </span>
                </div>
              </>
            )}
            onSelect={(index) => openDetail(index)}
          />
        </div>

        {/* CTA */}
        <Reveal delay={100} className="mt-14 md:mt-20">
          <div className="shine relative overflow-hidden rounded-3xl border border-border/70 bg-card/50 backdrop-blur-sm p-8 md:p-12 text-center">
            <div className="absolute left-1/2 top-0 h-48 w-[36rem] -translate-x-1/2 rounded-full bg-teal-600/20 blur-3xl" />
            <div className="relative">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold text-primary">
                <Sparkles size={13} />
                Got a vision?
              </div>
              <h3 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
                Have an idea worth building?
              </h3>
              <p className="mx-auto mt-3 max-w-xl text-base text-muted-foreground md:text-lg">
                Whether it&apos;s an agentic workflow, a RAG-powered product, or a full-scale
                platform — I&apos;d love to hear about it. Let&apos;s turn your vision into
                something intelligent together.
              </p>
              <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                <a
                  href="https://github.com/adeelahmed1293"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-target group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-6 py-3 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:from-teal-500 hover:to-emerald-500 hover:shadow-lg hover:shadow-teal-600/30"
                >
                  Follow on GitHub
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </a>
                <button
                  onClick={() =>
                    document.getElementById("contact").scrollIntoView({ behavior: "smooth" })
                  }
                  className="cursor-target inline-flex items-center justify-center gap-2 rounded-xl border border-primary/40 px-6 py-3 font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary/10"
                >
                  Discuss a project
                  <ArrowUpRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={selected.title}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />
          <div className="relative z-10 max-h-[85vh] w-full max-w-md overflow-y-auto rounded-3xl border border-border bg-card shadow-2xl">
            <button
              onClick={() => setSelected(null)}
              className="cursor-target absolute right-3 top-3 z-20 grid h-9 w-9 place-items-center rounded-full border border-border bg-background/85 text-foreground shadow-lg backdrop-blur-md transition-all duration-300 hover:scale-110 hover:border-primary/50 hover:text-primary active:scale-95"
              aria-label="Close project details"
            >
              <X size={16} />
            </button>

            <div className="p-4 md:p-5">
              <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                {selected.technologies[0]} &middot; core
              </div>
              <h3 className="font-display text-lg font-bold md:text-xl">{selected.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {selected.description}
              </p>

              {selected.features && (
                <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
                  {selected.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12.5px] text-muted-foreground">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      {feature}
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-3 flex flex-wrap gap-1.5">
                {selected.technologies.map((tech, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-border bg-secondary/50 px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-3 border-t border-border/60 pt-4">
                {selected.demo && (
                  <button
                    onClick={() => open(selected.demo)}
                    className="cursor-target inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg hover:shadow-teal-600/30 hover:brightness-110"
                  >
                    Live Demo
                    <ExternalLink size={14} />
                  </button>
                )}
                {selected.github && selected.github !== "#" && (
                  <button
                    onClick={() => open(selected.github)}
                    className="cursor-target inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-semibold text-muted-foreground transition-all duration-300 hover:border-primary/50 hover:text-primary"
                  >
                    <Github size={14} />
                    Code
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
